/**
 * Phase 83z.23 — Frontend error capture + reporter buffer.
 *
 * What it does:
 *   1. Wraps `toast.error()` so every error message also lands in an
 *      in-memory ring buffer (size 10, most-recent first).
 *   2. Captures uncaught window errors + unhandled promise rejections so
 *      even silent failures can be reported.
 *   3. Exposes `getRecentErrors()` and `submitErrorReport()` for the FAB
 *      and modal to consume.
 *
 * Why a ring buffer (not 1 single "lastError"):
 *   Users sometimes don't tap the FAB until 30s later. By then a second
 *   toast may have appeared. They can pick the one matching what they were
 *   doing from a 10-item list.
 */

import { toast as sonnerToast } from "sonner";
import { api } from "./api";

const BUFFER_SIZE = 10;
const STORAGE_KEY = "pickleague:errorBuffer:v1";

let _buffer = _loadBuffer();
const _listeners = new Set();

/* ──────────────────────────────────────────────────────────────────────────
 * Phase 85.A.14 — Role gate cho floating error toasts.
 *
 * User yêu cầu: KHÔNG hiển thị toast.error() cho VĐV (athlete) và guest.
 * Vẫn LOG ngầm về buffer/backend để debug, nhưng UI im lặng.
 * CHỈ hiển thị toast.error cho các role có nhu cầu vận hành:
 *   super_admin, admin, organizer, staff, referee.
 * ────────────────────────────────────────────────────────────────────── */
const PRIVILEGED_ROLES = new Set([
  "super_admin",
  "admin",
  "organizer",
  "staff",
  "referee",
]);
let _currentUserRoles = [];

/** Set bởi AuthProvider khi user thay đổi (login/logout/refresh). */
export function setCurrentUserRoles(roles) {
  _currentUserRoles = Array.isArray(roles) ? roles : [];
}

function _isPrivilegedUser() {
  if (!_currentUserRoles || _currentUserRoles.length === 0) return false;
  return _currentUserRoles.some((r) => PRIVILEGED_ROLES.has(r));
}

function _now() {
  return new Date().toISOString();
}

function _loadBuffer() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, BUFFER_SIZE) : [];
  } catch {
    return [];
  }
}

function _saveBuffer() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_buffer));
  } catch {
    /* sessionStorage may be unavailable in private mode */
  }
}

function _notify() {
  for (const cb of _listeners) {
    try {
      cb(_buffer);
    } catch {
      /* ignore listener errors */
    }
  }
}

function _push(entry) {
  _buffer = [entry, ..._buffer].slice(0, BUFFER_SIZE);
  _saveBuffer();
  _notify();
}

/** Push a string error into the buffer with auto-context. */
export function recordError(message, extra = {}) {
  if (!message) return;
  const text = typeof message === "string"
    ? message
    : (() => {
        try {
          return JSON.stringify(message);
        } catch {
          return String(message);
        }
      })();
  _push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    message: text.slice(0, 2000),
    timestamp: _now(),
    url: typeof window !== "undefined" ? window.location.href : "",
    ...extra,
  });
}

/** Return a snapshot of the latest N (default all) errors. */
export function getRecentErrors(n = BUFFER_SIZE) {
  return _buffer.slice(0, n);
}

/** Subscribe to buffer changes (used by the FAB badge count). */
export function subscribeErrors(cb) {
  _listeners.add(cb);
  cb(_buffer);
  return () => _listeners.delete(cb);
}

/** Clear all entries (after the user submits / dismisses the modal). */
export function clearErrors() {
  _buffer = [];
  _saveBuffer();
  _notify();
}

/** POST the report to backend. Always resolves; errors here would be
 * ironic so we just log them. */
export async function submitErrorReport({ message, description, context }) {
  try {
    const body = {
      message: message || "(empty)",
      description: description || null,
      url: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      context: context || {},
    };
    const r = await api.post("/error-reports", body);
    return { ok: true, id: r?.data?.id };
  } catch (e) {
    // Don't recurse — this is the reporter itself failing.
    // eslint-disable-next-line no-console
    console.warn("[errorReporter] submit failed", e);
    return { ok: false, error: e?.message || "submit_failed" };
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Sonner toast.error wrapper
 *
 * We patch the toast instance so every error message lands in the buffer.
 * The wrapper preserves the exact sonner API (positional + options arg).
 * ────────────────────────────────────────────────────────────────────── */
let _wrapped = false;
export function attachToastInterceptor() {
  if (_wrapped) return;
  _wrapped = true;
  const originalError = sonnerToast.error.bind(sonnerToast);
  sonnerToast.error = (...args) => {
    try {
      const [msg, opts] = args;
      const text = typeof msg === "string" ? msg : "(non-string error toast)";
      const desc = opts && typeof opts.description === "string" ? opts.description : null;
      // Phase 84.M.36 — Merge với entry api gần nhất (trong 2s) nếu có, để
      // tránh duplicate + giữ trọn context (status, url, response detail).
      const recent = _buffer[0];
      if (recent && recent.source === "api" && Date.now() - new Date(recent.timestamp).getTime() < 2000) {
        recent.toast_message = text.slice(0, 2000);
        recent.toast_description = desc;
        // Replace top entry to trigger listeners.
        _buffer = [{ ...recent }, ..._buffer.slice(1)];
        _saveBuffer();
        _notify();
      } else {
        recordError(text, { description: desc, source: "toast" });
      }
    } catch {
      /* swallow — never break the toast */
    }
    // Phase 85.A.14 — Chỉ hiện UI toast.error cho privileged roles
    // (super_admin/admin/organizer/staff/referee). VĐV và guest sẽ silent
    // fail: lỗi vẫn nằm trong buffer + được gửi BE qua API logging, nhưng
    // không nhảy float lên màn hình để giữ trải nghiệm sạch.
    if (!_isPrivilegedUser()) {
      return undefined;
    }
    return originalError(...args);
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Window-level safety net
 *
 * Catches genuinely uncaught errors (e.g. a render crash) so the user has
 * something to report. We DO NOT call toast here to avoid infinite loops.
 *
 * Phase 84.M.20.e — ChunkLoadError auto-recovery. Khi platform deploy
 * version mới, browser cache HTML cũ vẫn trỏ về chunks có hash cũ → CDN
 * trả 404 → ChunkLoadError. Strategy theo user choice 1a:
 *   • Lần 1: silent reload (sessionStorage flag chống vòng lặp).
 *   • Lần 2 (vẫn lỗi sau reload): toast warning với CTA "Tải lại".
 * ────────────────────────────────────────────────────────────────────── */
const CHUNK_RELOAD_FLAG = "pickleague:chunkReloadAttempted:v1";

function _isChunkLoadError(payload) {
  if (!payload) return false;
  const msg = (payload.message || "").toString();
  const fname = (payload.filename || "").toString();
  if (msg.includes("ChunkLoadError")) return true;
  if (msg.startsWith("Loading chunk") && msg.includes("failed")) return true;
  if (msg.startsWith("Loading CSS chunk") && msg.includes("failed")) return true;
  if (/\/static\/(js|css)\/[^/]+\.chunk\.(js|css)$/.test(fname)) return true;
  return false;
}

function _handleChunkLoadError(payload) {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
  let attempted = false;
  try {
    attempted = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "1";
  } catch {
    /* private mode */
  }
  if (!attempted) {
    // Lần 1: silent reload.
    try {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
    } catch { /* noop */ }
    // Defer 50ms để DevTools / Sentry kịp ghi log trước reload.
    setTimeout(() => window.location.reload(), 50);
    return;
  }
  // Lần 2+: vẫn lỗi → hiện CTA, ghi vào buffer.
  recordError(payload.message || "ChunkLoadError", {
    source: "chunk-load-error",
    filename: payload.filename,
  });
  try {
    sonnerToast.warning(
      "Hệ thống vừa cập nhật phiên bản mới — vui lòng tải lại trang.",
      {
        duration: Infinity,
        action: {
          label: "Tải lại",
          onClick: () => {
            try {
              sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
            } catch { /* noop */ }
            window.location.reload();
          },
        },
      },
    );
  } catch { /* noop */ }
}

let _windowAttached = false;
export function attachWindowErrorListeners() {
  if (_windowAttached) return;
  _windowAttached = true;
  if (typeof window === "undefined") return;
  // Phase 84.M.20.e — clear chunk-reload flag khi user load page mới thành
  // công (window load event fires AFTER all chunks loaded OK).
  window.addEventListener("load", () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch { /* noop */ }
  });
  // Phase 85.A.9 — Filter out 3rd-party / cross-origin / in-app browser noise.
  //
  // Common sources of noise (NOT bug của ứng dụng):
  //  • Zalo iOS in-app browser injects `zaloJSV2` SDK reference (ReferenceError)
  //  • Facebook in-app browser injects `_AutofillCallbackHandler`
  //  • MetaMask/extension inject `MetaMaskInpageProvider`
  //  • Cross-origin script errors → message="Script error.", filename=""
  //
  // Bỏ qua các pattern này để admin notifications không bị spam fake tickets.
  function _isThirdPartyNoise(payload) {
    const msg = (payload?.message || "").toString();
    const fname = (payload?.filename || "").toString();
    // Generic cross-origin script error (browsers obfuscate to "Script error.")
    if (msg === "Script error." && !fname) return true;
    // No filename + no line/col → almost always cross-origin script error
    if (!fname && !payload?.lineno && !payload?.colno) return true;
    // Known 3rd-party identifiers (in-app browsers / extensions / SDKs)
    const NOISE_TOKENS = [
      "zaloJSV2", "zaloJSV", "Zalo Mini App",
      "_AutofillCallbackHandler", "_AutofillCallback",
      "MetaMaskInpageProvider", "ethereum",
      "FBSDK", "fb_sdk", "fbq is not defined",
      "instantSearchSDK", "tikt0kjsapi", "tiktokSDK",
      "vivowebview", "MttJsBridge", "QBJSBridge",
      "huaweiclient", "samsungclient", "OperaJSCallback",
    ];
    return NOISE_TOKENS.some((t) => msg.includes(t) || fname.includes(t));
  }

  window.addEventListener("error", (event) => {
    if (!event) return;
    const payload = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    };
    if (_isChunkLoadError(payload)) {
      _handleChunkLoadError(payload);
      return;
    }
    if (_isThirdPartyNoise(payload)) {
      // Silently ignore — không log, không gửi BE.
      return;
    }
    recordError(event.message || "Unknown window error", {
      source: "window.error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const msg = typeof reason === "string"
      ? reason
      : reason?.message || "Unhandled promise rejection";
    const fname = reason?.fileName || reason?.filename || "";
    if (_isChunkLoadError({ message: msg, filename: fname })) {
      _handleChunkLoadError({ message: msg, filename: fname });
      return;
    }
    if (_isThirdPartyNoise({ message: msg, filename: fname })) {
      return;
    }
    recordError(msg, { source: "unhandledrejection" });
  });
}
